import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "../../components/Sections/HeroSection";
import StatisticsSection from "../../components/Sections/StatisticsSection";
import ServicesSection from "../../components/Sections/ServicesSection";
import FeaturesSection from "../../components/Sections/FeaturesSection";
import ProcessSection from "../../components/Sections/ProcessSection";
import ToolsSection from "../../components/Sections/ToolsSection";
import BenefitsSection from "../../components/Sections/BenefitsSection";
import CTASection from "../../components/Sections/CTASection";
import HorizontalScroller from "../../components/Sections/HorizontalScroller";
import { NextSeo } from "next-seo";
import { Feature, ProcessStep, ScrollerItem } from "../../utils/types";

const Ecommerce: React.FC = () => {
  const statistics = [
    {
      title: "Increase in Sales",
      description:
        "Our strategies led to a significant boost in sales for our clients.",
      value: "200%",
    },
    {
      title: "Customer Retention",
      description: "We helped retain customers, increasing overall loyalty.",
      value: "30%",
    },
    {
      title: "Reduced Cart Abandonment",
      description:
        "Our techniques minimized cart abandonment rates effectively.",
      value: "27%",
    },
    {
      title: "Improved SEO Rankings",
      description:
        "Clients saw a substantial improvement in search engine rankings.",
      value: "64%",
    },
  ];

  const services = [
    {
      title: "E-commerce Platform Development",
      description:
        "We'll build you a virtual storefront that's as unique as your brand and as easy to navigate as a treasure map.",
    },
    {
      title: "Shopping Cart and Checkout Optimization",
      description:
        "We'll create a seamless checkout experience that'll make your customers feel like they just scored the deal of the century.",
    },
    {
      title: "Inventory Management Systems",
      description:
        "We'll create a seamless checkout experience that'll make your customers feel like they just scored the deal of the century.",
    },
    {
      title: "Personalized Product Recommendations",
      description:
        "We'll use AI to suggest products your customers will love, turning casual browsers into loyal shoppers.",
    },
    {
      title: "Customer Relationship Management (CRM) Software",
      description:
        "We'll help you build lasting relationships with your customers, nurturing leads and turning them into raving fans.",
    },
    {
      title: "Data Analytics and Reporting",
      description:
        "We'll turn your data into a gold mine of insights, uncovering hidden opportunities and guiding your business decisions.",
    },
  ];

  const features: Feature[] = [
    {
      title: "Hyper-Personalized Recommendations",
      description:
        "We'll use AI to suggest products your customers will love, turning casual browsers into loyal shoppers.",
      imageUrl: "/icons/Personalization1.png", // Replace with your actual SVG path
    },
    {
      title: "Dynamic Pricing Optimization",
      description:
        "Maximize your profits by adjusting prices in real time based on demand, inventory, and competitor data.",
      imageUrl: "/icons/ROI.png", // Replace with your actual SVG path
    },
    {
      title: "Chatbots & Virtual Assistants",
      description:
        "Provide instant customer support, answer questions, and guide shoppers through their purchase journey.",
      imageUrl: "/icons/Chatbot1.png", // Replace with your actual SVG path
    },
    {
      title: "Fraud Detection and Prevention",
      description:
        "Protect your business from fraudulent transactions and chargebacks with AI-powered algorithms.",
      imageUrl: "/icons/25-shopping-online_128.png", // Replace with your actual SVG path
    },
    {
      title: "Predictive Analytics",
      description:
        "Forecast trends, identify opportunities, and make data-driven decisions that drive growth.",
      imageUrl: "/icons/Analysis1.png", // Replace with your actual SVG path
    },
    {
      title: "Customer Segmentation & Targeting",
      description:
        "Identify your most valuable customers and tailor your marketing messages to their specific needs and interests.",
      imageUrl: "/icons/Target Audience.png", // Replace with your actual SVG path
    },
  ];

  const steps: ProcessStep[] = [
    {
      title: "Fashion and apparel",
      description: "Fashion and apparel",
      icon: "/icons/fashion.png",
    },
    {
      title: "Health and beauty",
      description: "Health and beauty.",
      icon: "/icons/beauty.png",
    },
    {
      title: "Electronics",
      description: "Electronics",
      icon: "/icons/electronics.png",
    },
    {
      title: "Home goods",
      description: "Home goods",
      icon: "/icons/home.png",
    },
    {
      title: "Food and beverage",
      description: "Food and beverage",
      icon: "/icons/food.png",
    },
    {
      title: "Grocery",
      description: "Grocery",
      icon: "/icons/grocery1.png",
    },
  ];

  const tools = [
    {
      name: "Python",
      icon: "/icons/python1.svg",
    },
    {
      name: "Django",
      icon: "/icons/Django.svg",
    },
    {
      name: "CSS",
      icon: "/icons/css.svg",
    },
    {
      name: "HTML",
      icon: "/icons/html.svg",
    },
    {
      name: "FastAPI",
      icon: "/icons/FastAPI.svg",
    },
    {
      name: "JavaScript",
      icon: "/icons/js.svg",
    },
    {
      name: "GraphQL",
      icon: "/icons/GraphQL.png",
    },
    {
      name: "PostgreSQL",
      icon: "/icons/postgresql.png",
    },
    {
      name: "MongoDB",
      icon: "/icons/MongoDB.png",
    },
    {
      name: "Node JS",
      icon: "/icons/NodeJS.svg",
    },
    {
      name: "NextJS",
      icon: "/icons/Next.png",
    },
    {
      name: "React",
      icon: "/icons/react.svg",
    },
  ];

  const benefits = [
    {
      title: "SEO Gurus",
      description:
        "Dominate search rankings with our SEO strategies. We make Google love you!",
      icon: "/icons/SEO.png",
    },
    {
      title: "Retention Wizards",
      description:
        "Boost customer loyalty with our tailored engagement strategies. We turn shoppers into fans.",
      icon: "/icons/retention.png",
    },
    {
      title: "Omnichannel Gurus",
      description:
        "Seamlessly connect all sales channels. We make selling everywhere a breeze.",
      icon: "/icons/OMNICHANNEL.PNG",
    },
    {
      title: "Cart Savers",
      description:
        "Slash cart abandonment with our savvy tactics. We turn abandoned carts into cha-ching!",
      icon: "/icons/CART.png",
    },
    {
      title: "Competitive Edge",
      description:
        "Outshine rivals with our market insights. We turn competition into ancient history.",
      icon: "/icons/edge.png",
    },
    {
      title: "Ad Magicians",
      description:
        "Maximize ROI with our marketing prowess. We turn every ad dollar into pure gold.",
      icon: "/icons/ad.png",
    },
    {
      title: "Inventory & Pricing Wizards",
      description:
        "Automate inventory and dynamic pricing to stay ahead. No more stock headaches, just smart profits!",
      icon: "/icons/ROI.png",
    },
    {
      title: "Tech Whisperers",
      description:
        "Keep up with tech trends effortlessly. We future-proof your business.",
      icon: "/icons/tech.png",
    },
  ];

  const scrollerItems: ScrollerItem[] = [
    { title: "Showrooming Online", icon: "/icons/Showrooming.png" },
    { title: "Responsive Website", icon: "/icons/responsivepng.png" },
    { title: "Growth Hacking", icon: "/icons/Growth.png" },
    { title: "Email Marketing", icon: "/icons/Email.png" },
    { title: "User Engagement", icon: "/icons/User.png" },
    { title: "Social Marketing", icon: "/icons/Social.png" },
    { title: "Shopper Personalization", icon: "/icons/Personalization.png" },
    { title: "Sales Funnel", icon: "/icons/SalesFunnel.png" },
  ];

  return (
    <>
      <NextSeo
        title="E-commerce Management Solution | PlanetHive AI"
        description="Master your online store operations with our custom e-commerce solutions and drive growth."
        canonical="https://www.planethive.ai/industries/ecommerce"
        openGraph={{
          url: "https://www.planethive.ai/industries/ecommerce",
          title: "E-commerce Management Solution | PlanetHive AI",
          description:
            "Master your online store operations with our custom e-commerce solutions. Streamline operations, make smarter business decisions, and drive growth.",
          images: [
            {
              url: "https://www.planethive.ai/images/planet-hive-logo.png",
              width: 421,
              height: 120,
              alt: "E-commerce Management Solution",
            },
          ],
        }}
      />
      <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "E-commerce Management Solution | PlanetHive AI",
      description:
        "Master your online store operations with our custom e-commerce solutions. Streamline operations, make smarter business decisions, and drive growth.",
      url: "https://www.planethive.ai/industries/ecommerce",
      mainEntity: {
        "@type": "Service",
        name: "E-commerce Management Solution",
        description:
          "PlanetHive AI offers tailored e-commerce management solutions, including platform development, personalized product recommendations, dynamic pricing optimization, and AI-powered customer insights.",
        provider: {
          "@type": "Organization",
          name: "Planet Hive",
          url: "https://www.planethive.ai",
          logo: "https://www.planethive.ai/images/planet-hive-logo.png",
        },
        areaServed: {
          "@type": "Place",
          name: "Global",
        },
        audience: {
          "@type": "Audience",
          audienceType: [
            "Fashion and Apparel Businesses",
            "Health and Beauty Stores",
            "Electronics Retailers",
            "Home Goods Sellers",
            "Food and Beverage Businesses",
            "Grocery Stores",
          ],
        },
        availableChannel: {
          "@type": "ServiceChannel",
          serviceUrl: "https://www.planethive.ai/contact",
        },
        termsOfService: "https://www.planethive.ai/terms-conditions",
        keywords: [
          "e-commerce AI",
          "AI shopping cart optimization",
          "personalized recommendations",
          "dynamic pricing for retail",
          "e-commerce platform development",
          "customer analytics for online stores",
          "inventory management solutions",
        ],
      },
      potentialAction: {
        "@type": "ContactAction",
        name: "Contact Planet Hive for E-commerce Solutions",
        target: "https://www.planethive.ai/contact",
      },
      video: {
        "@type": "VideoObject",
        name: "AI-Powered E-commerce Transformation",
        description:
          "Discover how Planet Hive revolutionizes e-commerce with AI-powered tools for smarter operations, better customer retention, and higher sales.",
        thumbnailUrl:
          "https://www.planethive.ai/images/ecommerce-thumb.jpg",
        uploadDate: "2024-01-15",
        contentUrl: "/video/particles.mp4",
        embedUrl: "/video/particles.mp4",
        duration: "PT3M15S",
      },
      mainEntityOfPage: {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What services does Planet Hive provide for e-commerce businesses?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Planet Hive offers e-commerce platform development, AI-driven personalization, dynamic pricing optimization, and customer analytics to help businesses grow.",
            },
          },
          {
            "@type": "Question",
            name: "How does AI improve the customer shopping experience?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AI enhances the shopping experience by providing personalized recommendations, optimizing pricing in real time, and offering chatbot support for seamless interactions.",
            },
          },
          {
            "@type": "Question",
            name: "What industries does Planet Hive cater to?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "We work with a wide range of industries, including fashion and apparel, health and beauty, electronics, home goods, food and beverage, and grocery businesses.",
            },
          },
          {
            "@type": "Question",
            name: "Can Planet Hive help reduce cart abandonment rates?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, our strategies are designed to minimize cart abandonment by optimizing the checkout process, offering real-time support, and personalizing the shopping journey.",
            },
          },
          {
            "@type": "Question",
            name: "How does dynamic pricing optimization benefit retailers?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Dynamic pricing adjusts product prices in real time based on market demand, inventory levels, and competitor pricing, maximizing revenue and staying competitive.",
            },
          },
        ],
      },
    }),
  }}
/>

      
      <Header />
      <main className="bg-[#10031dff] text-white">
        <HeroSection
          mediaType="video"
          mediaUrl="/video/particles.mp4"
          title="Planet Hive: AI-Powered Retail Magic"
          description="Tired of tech headaches? Let's transform your store with AI. Say hello to smart, stylish, and seamless shopping!"
          buttonText="Join Us Today"
          buttonLink="/contact"
        />
        <ServicesSection
          title="Lost in Retail Chaos? We've Got Your Lifeline!"
          description="Running a retail business feels like juggling torches on a unicycle. Fear not! Planet Hive crafts custom software to smooth out the chaos."
          services={services}
        />

        <StatisticsSection
          title="Key Statistics"
          description="The Proof is in the Pudding (Or the Sales Figures). Don't just take our word for it. Our E-commerce solutions have helped businesses achieve:"
          statistics={statistics}
        />
        <FeaturesSection
          title="Power Up Your E-commerce<br>With AI Magic (Marketing Genie Included)"
          features={features}
        />
        <ProcessSection
          title="Whom Do We Cater To?"
          description="We serve a wide range of e-commerce businesses, including"
          steps={steps}
        />
        <HorizontalScroller items={scrollerItems} />
        <ToolsSection
          title="Our Tech Stack<br> Where Innovation Meets Mastery!"
          tools={tools}
        />
        <BenefitsSection
          title="Why Choose Planet Hive?<br>We’re Not Just Techies, We’re Problem Solvers!"
          benefits={benefits}
        />
        <CTASection
          modelUrl="/spaceRobotanimate.glb" // Replace with your 3D model path
          title="Ready to Transform Your Business?"
          description="Get in touch for a free consultation and see how we can make magic happen!"
        />
      </main>
      <Footer />
    </>
  );
};

export default Ecommerce;
